import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ReportTheme } from '../../types';
import { getAreaChartColors } from './chartUtils';
import { validateDateKeyedData, sortChartDataByDate } from './functionalWidgets';

interface TotalTrafficAreaChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const TotalTrafficAreaChartWidget: React.FC<TotalTrafficAreaChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {

  // Custom date filtering method for this widget
  const filterDataByDateRange = (entries: [string, any][], startDate?: string, endDate?: string): [string, any][] => {
    if (!startDate || !endDate) {
      console.log('TotalTrafficAreaChartWidget: No date range provided, returning all entries');
      return entries;
    }

    // Generate all date strings within the range
    const generateDateStrings = (start: string, end: string): Set<string> => {
      console.log('TotalTrafficAreaChartWidget: Generating date strings for range:', start, 'to', end);
      const dateStrings = new Set<string>();
      const startDate = new Date(start);
      const endDate = new Date(end);
      
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const dateString = `${year}${month}${day}`;
        
        dateStrings.add(dateString);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return dateStrings;
    };

    const validDateStrings = generateDateStrings(startDate, endDate);
    console.log(`TotalTrafficAreaChartWidget: Generated ${validDateStrings.size} valid date strings for range ${startDate} to ${endDate}`);
    
    const filteredEntries = entries.filter(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object') {
        return false;
      }
      
      // Check if dateKey is in the valid date strings set
      return validDateStrings.has(dateKey);
    });
    
    console.log(`TotalTrafficAreaChartWidget: Filtered ${filteredEntries.length} entries from ${entries.length} total entries for date range ${startDate} to ${endDate}`);
    
    return filteredEntries;
  };

  // Process the raw API data internally for Sessions vs Pageviews
  const processData = () => {
    console.log('TotalTrafficAreaChartWidget: Processing data with date range:', report_start_date, 'to', report_end_date);
    
    // Validate data structure and get entries
    const validation = validateDateKeyedData(data);
    if (!validation.isValid) {
      console.warn(`TotalTrafficAreaChartWidget: ${validation.error}`);
      return null;
    }

    console.log('TotalTrafficAreaChartWidget: Total entries before filtering:', validation.entries.length);

    // Filter data by date range using custom method
    const filteredEntries = filterDataByDateRange(validation.entries, report_start_date, report_end_date);
    
    console.log('TotalTrafficAreaChartWidget: Filtered entries after date filtering:', filteredEntries, filteredEntries.length);
    
    if (filteredEntries.length === 0) {
      console.warn('TotalTrafficAreaChartWidget: No data entries found in date range');
      return null;
    }

    // Validate the first entry to ensure it has the required metrics
    const firstEntry = filteredEntries[0];
    const [, firstDayData] = firstEntry;
    
    // Validate that we have the required metrics (sessions and page_views)
    if (typeof (firstDayData as any).sessions !== 'number' || typeof (firstDayData as any).page_views !== 'number') {
      console.warn('TotalTrafficAreaChartWidget: Missing required metrics (sessions, page_views)');
      return null;
    }
    
    // Convert the date-keyed object to an array of chart data points
    const chartData = filteredEntries.map(([dateKey, dayData]: [string, any]) => {
      // Validate each entry
      if (!dayData || typeof dayData !== 'object' || !dayData.date) {
        console.warn(`TotalTrafficAreaChartWidget: Invalid entry for date ${dateKey}`);
        return null;
      }

      const result: { name: string; sessions: number; page_views: number; date: string } = {
        name: new Date(dayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        sessions: dayData.sessions || 0,
        page_views: dayData.page_views || 0,
        date: dayData.date // Keep original date for sorting
      };

      return result;
    }).filter((item: any): item is { name: string; sessions: number; page_views: number; date: string } => item !== null);
    
    // Sort by date using utility function
    const sortedData = sortChartDataByDate(chartData);
    
    console.log('TotalTrafficAreaChartWidget: Final chart data:', sortedData);
    console.log('TotalTrafficAreaChartWidget: Date range in chart data:', sortedData.length > 0 ? `${sortedData[0].name} to ${sortedData[sortedData.length - 1].name}` : 'No data');
    
    return sortedData.length > 0 ? sortedData : null;
  };

  const processedData = processData();
  

  // Don't render if data is not in the expected format
  if (!processedData || processedData.length === 0) {
    return null;
  }

  
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={processedData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 12 }}
          interval="preserveStartEnd"
        />
        <YAxis />
        <Tooltip />
        <Area 
          type="monotone" 
          dataKey="sessions" 
          stroke={getAreaChartColors(0, reportTheme.theme).stroke}
          fill={getAreaChartColors(0, reportTheme.theme).fill}
          fillOpacity={getAreaChartColors(0, reportTheme.theme).fillOpacity}
          name="Sessions"
        />
        <Area 
          type="monotone" 
          dataKey="page_views" 
          stroke={getAreaChartColors(1, reportTheme.theme).stroke}
          fill={getAreaChartColors(1, reportTheme.theme).fill}
          fillOpacity={getAreaChartColors(1, reportTheme.theme).fillOpacity}
          name="Page Views"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
